import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Leaf } from 'lucide-react';
import { ingredients } from '@/data/ingredients';
import { DICT } from '@/constants/testIds';

export default function IngredientDictionary() {
  return (
    <div
      data-testid={DICT.root}
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden"
    >
      <div className="blob absolute -top-24 -left-24 w-[500px] h-[500px] rounded-full bg-[#2D8B5B]" style={{ opacity: 0.14 }} />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <Link
          to="/"
          data-testid={DICT.back}
          className="inline-flex items-center gap-2 text-sm text-[#1D7448] font-semibold link-sweep"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-8 max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.24em] text-[#2D8B5B] mb-4">
            Ingredient Dictionary
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#1D7448] font-light leading-[1.02]">
            A quiet library <span className="italic">of the herbs we love.</span>
          </h1>
          <p className="mt-6 text-lg text-[#4A6756] leading-relaxed max-w-2xl">
            Every Sumaya blend is built from a small, considered pantry of
            ingredients — chosen for what they do, where they come from, and how
            they meet the body.
          </p>
        </motion.div>

        <div className="mt-16 md:mt-20 space-y-16 md:space-y-24">
          {ingredients.map((ing, i) => (
            <motion.article
              key={ing.slug}
              data-testid={DICT.card(ing.slug)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8 }}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center ${
                i % 2 === 1 ? 'md:[direction:rtl]' : ''
              }`}
            >
              <div className="md:col-span-5 md:[direction:ltr]">
                <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden group">
                  <img
                    src={ing.image}
                    alt={ing.name}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D3A24]/40 via-transparent to-transparent" />
                  <div className="absolute top-5 left-5 text-[11px] uppercase tracking-[0.22em] bg-[#FAF4E3]/90 backdrop-blur px-3 py-1.5 rounded-full text-[#1D7448] border border-[#E5DCC3]">
                    {ing.origin}
                  </div>
                </div>
              </div>

              <div className="md:col-span-7 md:[direction:ltr]">
                <p className="text-xs uppercase tracking-[0.24em] text-[#2D8B5B] mb-3">
                  {ing.short}
                </p>
                <h2 className="font-serif text-4xl md:text-5xl text-[#1D7448] leading-tight">
                  {ing.name}
                </h2>
                <p className="mt-6 text-[17px] text-[#0D3A24] leading-relaxed max-w-xl">
                  {ing.detail}
                </p>
                <div className="mt-8">
                  <p className="text-xs uppercase tracking-[0.22em] text-[#4A6756] mb-3">
                    Found in
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ing.teas.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full bg-[#F3EAD3] text-[#1D7448] font-medium"
                      >
                        <Leaf className="w-3.5 h-3.5" /> {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}
